import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ContentLoader } from '@rio-cloud/rio-uikit';

import './styles.css';

export type SelectOption = {
    value: string;
    label: string;
    disabled?: boolean;
};

type SelectProps = {
    value: string;
    options: SelectOption[];
    onChange: (value: string) => void;
    ariaLabel: string;
    emptyLabel?: string;
    prefix?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    loading?: boolean;
};

export default function Select({
    value,
    options,
    onChange,
    ariaLabel,
    emptyLabel,
    prefix,
    disabled = false,
    fullWidth = false,
    loading = false,
}: SelectProps) {
    const intl = useIntl();
    const rootRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const selectedIndex = options.findIndex(option => option.value === value);
    const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : (emptyLabel ?? intl.formatMessage({ id: 'intl-msg:support.aeg.select.placeholder' }));
    const menuOptions = emptyLabel ? [{ value: '', label: emptyLabel }, ...options] : options;
    const selectedMenuIndex = Math.max(
        0,
        menuOptions.findIndex(option => option.value === value)
    );

    useEffect(() => {
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, []);

    useEffect(() => {
        if (loading) {
            setOpen(false);
        }
    }, [loading]);

    useEffect(() => {
        if (open) {
            setHighlightedIndex(selectedMenuIndex);
        }
    }, [open, selectedMenuIndex]);

    const select = (option: SelectOption) => {
        if (option.disabled) {
            return;
        }
        onChange(option.value);
        setOpen(false);
    };

    const moveHighlight = (direction: 1 | -1) => {
        setHighlightedIndex(current => {
            for (let step = 1; step <= menuOptions.length; step += 1) {
                const next = (current + direction * step + menuOptions.length) % menuOptions.length;
                if (!menuOptions[next]?.disabled) {
                    return next;
                }
            }
            return current;
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (!open && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            setOpen(true);
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveHighlight(1);
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveHighlight(-1);
        }
        if (open && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            const option = menuOptions[highlightedIndex];
            if (option) {
                select(option);
            }
        }
        if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div className={`aeg-select-root ${fullWidth ? 'width-100pct' : ''} ${value ? 'active' : ''}`} ref={rootRef}>
            <button
                aria-busy={loading}
                aria-expanded={open}
                aria-haspopup='menu'
                aria-label={loading ? ariaLabel : `${ariaLabel}: ${selectedLabel}`}
                className={`aeg-select-trigger display-flex align-items-center gap-5 text-color-darkest ${
                    fullWidth ? 'height-40' : ''
                } ${value && !fullWidth && !loading ? 'bg-highlight-lighter' : 'bg-white'}`}
                disabled={disabled || loading}
                onClick={() => setOpen(current => !current)}
                onKeyDown={handleKeyDown}
                type='button'
            >
                <strong className='aeg-select-value text-color-black'>
                    {loading ? (
                        <ContentLoader height={16} width={140} />
                    ) : (
                        <>
                            {prefix ? `${prefix} ` : ''}
                            {selectedLabel}
                        </>
                    )}
                </strong>
                <span
                    aria-hidden='true'
                    className={`rioglyph ${open ? 'rioglyph-chevron-up' : 'rioglyph-chevron-down'} aeg-select-chevron text-color-dark`}
                />
            </button>
            {open ? (
                <div className='aeg-select-menu bg-white'>
                    {menuOptions.map((option, index) => {
                        const selected = option.value === value;
                        const highlighted = index === highlightedIndex;
                        return (
                            <button
                                aria-pressed={selected}
                                className={`aeg-select-option ${
                                    selected ? 'selected text-color-highlight-dark' : 'text-color-darkest'
                                } ${
                                    option.disabled
                                        ? ''
                                        : selected && highlighted
                                          ? 'bg-highlight-lighter'
                                          : highlighted
                                            ? 'bg-lighter'
                                            : 'bg-white'
                                }`}
                                disabled={option.disabled}
                                key={`${option.value}-${option.label}`}
                                onMouseEnter={() => {
                                    if (!option.disabled) {
                                        setHighlightedIndex(index);
                                    }
                                }}
                                onClick={() => select(option)}
                                type='button'
                            >
                                <span aria-hidden='true' className='aeg-select-check text-color-highlight-dark'>
                                    {selected ? '✓' : ''}
                                </span>
                                <span>{option.label}</span>
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
